/**
 * Legal Content Data - Edit this file to update legal documents
 * All content should be reviewed by legal counsel before publication
 */

export const legalDocuments = [
  {
    id: 'terms',
    title: 'Terms of Service',
    description: 'The rules and guidelines for using the Flippex platform',
    path: '/legal/terms',
    icon: 'FileText'
  },
  {
    id: 'privacy',
    title: 'Privacy Policy',
    description: 'How we collect, use, and protect your personal data',
    path: '/legal/privacy',
    icon: 'Shield'
  },
  {
    id: 'cookies',
    title: 'Cookie Policy',
    description: 'Information about cookies and tracking technologies',
    path: '/legal/cookies',
    icon: 'Cookie'
  },
  {
    id: 'risk',
    title: 'Risk Disclosure',
    description: 'Important information about investment risks',
    path: '/legal/risk',
    icon: 'AlertTriangle'
  }
];

export const termsOfService = {
  lastUpdated: 'January 2025',
  sections: [
    {
      id: 'introduction',
      title: '1. Introduction and Acceptance',
      content: `Welcome to Flippex. These Terms of Service ("Terms") govern your access to and use of the Flippex platform, website, and related services (collectively, the "Platform").

By accessing or using the Platform, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Platform.

Flippex reserves the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on the Platform and updating the "Last Updated" date. Your continued use of the Platform after such changes constitutes acceptance of the modified Terms.`
    },
    {
      id: 'eligibility',
      title: '2. Eligibility',
      content: `To use the Platform, you must:

• Be at least 18 years of age (or the age of legal majority in your jurisdiction)
• Have the legal capacity to enter into binding contracts
• Not be prohibited from using the Platform under applicable laws
• Successfully complete our identity verification (KYC) process where required
• Provide accurate and complete registration information

Certain investment projects may have additional eligibility requirements based on your jurisdiction, investor status, or other factors. These requirements will be disclosed on the relevant project pages.`
    },
    {
      id: 'account',
      title: '3. Account Registration and Security',
      content: `You are responsible for:

• Maintaining the confidentiality of your account credentials
• All activities that occur under your account
• Notifying us immediately of any unauthorized access or security breach
• Ensuring your account information remains accurate and up-to-date

Flippex reserves the right to suspend or terminate accounts that violate these Terms or pose a security risk.`
    },
    {
      id: 'platform-role',
      title: '4. Platform Role and Limitations',
      content: `Flippex operates as a marketplace connecting investors with real estate investment projects. Important limitations:

• Flippex is NOT a bank, broker-dealer, or investment advisor
• We do NOT provide personalized investment advice
• Information on the Platform is for informational purposes only
• You are solely responsible for your investment decisions
• We recommend consulting qualified financial, tax, and legal advisors before investing

Flippex facilitates transactions but does not guarantee any investment outcomes.`
    },
    {
      id: 'project-information',
      title: '5. Project Information and Disclaimers',
      content: `Project information on the Platform is provided by project sponsors and operators. While we conduct due diligence:

• We do NOT guarantee the accuracy, completeness, or reliability of project information
• Projected returns, timelines, and financial forecasts are estimates only
• Past performance is not indicative of future results
• Forward-looking statements involve known and unknown risks
• Actual results may differ materially from projections

You should independently verify all information and read all project documentation before investing.`
    },
    {
      id: 'investments',
      title: '6. Investments and Commitments',
      content: `When you commit to an investment:

• Your commitment is subject to the specific terms disclosed on the project page
• Minimum investment amounts, holding periods, and other terms vary by project
• Cancellation rights, if any, are specified in the project documentation
• Tokens or units represent your interest as described in the offering documents
• The legal structure (e.g., SPV) and your rights are defined in project documents

Flippex is not responsible for the performance or management of individual projects.`
    },
    {
      id: 'fees',
      title: '7. Fees and Payments',
      content: `Fees associated with Platform use and investments may include:

• Platform fees (disclosed per project)
• Setup or structuring fees
• Management fees
• Performance fees (where applicable)
• Transaction fees

All applicable fees are disclosed on the relevant project page before you invest. Fee structures may vary between projects.`
    },
    {
      id: 'prohibited',
      title: '8. Prohibited Uses',
      content: `You agree NOT to:

• Use the Platform for any unlawful purpose
• Violate any applicable laws or regulations
• Provide false or misleading information
• Attempt to gain unauthorized access to the Platform
• Interfere with the Platform's operation or security
• Use automated systems to access the Platform without permission
• Engage in market manipulation or fraudulent activities
• Circumvent any access restrictions or security measures`
    },
    {
      id: 'ip',
      title: '9. Intellectual Property',
      content: `All content on the Platform, including text, graphics, logos, and software, is the property of Flippex or its licensors and is protected by intellectual property laws.

You may not:
• Copy, modify, or distribute Platform content without permission
• Use Flippex trademarks without written consent
• Reverse engineer or decompile Platform software
• Remove any copyright or proprietary notices`
    },
    {
      id: 'third-party',
      title: '10. Third-Party Services',
      content: `The Platform may integrate with or link to third-party services (e.g., payment processors, identity verification providers). Your use of such services is subject to their respective terms and privacy policies.

Flippex is not responsible for the availability, accuracy, or practices of third-party services.`
    },
    {
      id: 'disclaimers',
      title: '11. Disclaimers and Limitation of Liability',
      content: `THE PLATFORM IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.

TO THE MAXIMUM EXTENT PERMITTED BY LAW:

• Flippex disclaims all warranties, including merchantability and fitness for a particular purpose
• Flippex is not liable for any indirect, incidental, special, or consequential damages
• Flippex's total liability shall not exceed the fees you paid to Flippex in the 12 months preceding the claim
• Investment losses are your responsibility and are not covered by these limitations`
    },
    {
      id: 'indemnity',
      title: '12. Indemnification',
      content: `You agree to indemnify and hold harmless Flippex, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from:

• Your use of the Platform
• Your violation of these Terms
• Your violation of any applicable laws
• Your investment activities`
    },
    {
      id: 'termination',
      title: '13. Termination',
      content: `Flippex may suspend or terminate your access to the Platform at any time for:

• Violation of these Terms
• Suspected fraudulent or illegal activity
• Failure to complete required verification
• Extended inactivity
• Any other reason at our sole discretion

Upon termination, your right to use the Platform ceases immediately. Existing investment positions will be handled according to their respective terms.`
    },
    {
      id: 'governing-law',
      title: '14. Governing Law and Disputes',
      content: `These Terms are governed by the laws of Portugal.

Any disputes arising from these Terms or your use of the Platform shall be resolved through:

1. Good faith negotiation between the parties
2. Mediation, if negotiation fails
3. The courts of Lisbon, Portugal, which shall have exclusive jurisdiction

Nothing in this section limits your statutory rights as a consumer.`
    },
    {
      id: 'contact',
      title: '15. Contact Information',
      content: `For questions about these Terms, please contact us:

Flippex
Lisbon, Portugal
Email: legal@flippex.com

For general support inquiries, visit our Contact page or email support@flippex.com.`
    }
  ]
};

export const privacyPolicy = {
  lastUpdated: 'January 2025',
  sections: [
    {
      id: 'introduction',
      title: '1. Introduction',
      content: `Flippex ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal data when you use our platform and services.

This policy applies to all users of the Flippex platform, including visitors, registered users, and investors.`
    },
    {
      id: 'data-collected',
      title: '2. Data We Collect',
      content: `We collect the following categories of personal data:

**Account Information**
• Name, email address, phone number
• Date of birth, nationality, country of residence
• Username and password (encrypted)

**Verification Data (KYC/AML)**
• Government-issued ID documents
• Proof of address
• Tax identification numbers
• Source of funds information

**Financial Information**
• Bank account details for distributions
• Investment history and holdings
• Transaction records

**Usage Data**
• IP address and device information
• Browser type and settings
• Pages visited and features used
• Timestamps and session duration

**Communications**
• Support inquiries and responses
• Email preferences and subscriptions`
    },
    {
      id: 'how-we-use',
      title: '3. How We Use Your Data',
      content: `We process your personal data for the following purposes:

**Service Provision**
• Creating and managing your account
• Processing investments and distributions
• Providing access to project information

**Compliance**
• Identity verification (KYC/AML)
• Regulatory reporting requirements
• Fraud prevention and detection

**Security**
• Protecting your account and data
• Monitoring for suspicious activity
• Maintaining platform integrity

**Communication**
• Sending transaction confirmations
• Project updates and notifications
• Service announcements
• Marketing (with your consent)`
    },
    {
      id: 'legal-basis',
      title: '4. Legal Basis for Processing',
      content: `Under GDPR, we process your data based on:

**Contract Performance**
• Processing necessary to provide our services to you

**Legal Obligations**
• Compliance with KYC/AML regulations
• Tax reporting requirements
• Regulatory requests

**Legitimate Interests**
• Fraud prevention and security
• Platform improvement and analytics
• Business operations

**Consent**
• Marketing communications
• Optional data processing activities`
    },
    {
      id: 'sharing',
      title: '5. Data Sharing',
      content: `We may share your data with:

**Service Providers**
• Identity verification providers
• Payment processors
• Cloud hosting services
• Customer support tools

**Compliance Partners**
• Regulatory authorities (when required by law)
• Legal advisors
• Auditors

**Project Partners**
• Project sponsors may receive limited investor information necessary for reporting and distributions

We do NOT sell your personal data to third parties.`
    },
    {
      id: 'retention',
      title: '6. Data Retention',
      content: `We retain your personal data for:

• **Active accounts**: As long as your account remains active
• **Compliance records**: As required by applicable laws (typically 5-10 years for financial records)
• **Closed accounts**: Limited retention for legal and regulatory purposes

You may request deletion of your data subject to our legal retention obligations.`
    },
    {
      id: 'security',
      title: '7. Security Measures',
      content: `We implement appropriate security measures including:

• Encryption of data in transit and at rest
• Secure authentication mechanisms
• Access controls and audit logging
• Regular security assessments
• Employee training and access restrictions

While we strive to protect your data, no system is completely secure. We encourage you to use strong passwords and protect your account credentials.`
    },
    {
      id: 'your-rights',
      title: '8. Your Rights',
      content: `Under GDPR, you have the right to:

• **Access**: Request a copy of your personal data
• **Rectification**: Correct inaccurate or incomplete data
• **Erasure**: Request deletion of your data (subject to legal obligations)
• **Portability**: Receive your data in a structured format
• **Restriction**: Limit how we process your data
• **Objection**: Object to certain processing activities
• **Withdraw Consent**: Revoke previously given consent

To exercise these rights, contact us at privacy@flippex.com.`
    },
    {
      id: 'international',
      title: '9. International Transfers',
      content: `Your data may be transferred to and processed in countries outside the European Economic Area (EEA).

When we transfer data internationally, we ensure appropriate safeguards through:
• Standard Contractual Clauses (SCCs)
• Adequacy decisions
• Other approved transfer mechanisms`
    },
    {
      id: 'contact',
      title: '10. Contact Us',
      content: `For privacy-related inquiries:

**Data Protection Officer**
Email: privacy@flippex.com

**Supervisory Authority**
You have the right to lodge a complaint with your local data protection authority. In Portugal, this is the Comissão Nacional de Proteção de Dados (CNPD).`
    }
  ]
};

export const cookiePolicy = {
  lastUpdated: 'January 2025',
  sections: [
    {
      id: 'what-are-cookies',
      title: '1. What Are Cookies?',
      content: `Cookies are small text files stored on your device when you visit a website. They help websites remember your preferences and understand how you use the site.

Similar technologies include:
• Local storage
• Session storage
• Pixels and beacons`
    },
    {
      id: 'types',
      title: '2. Types of Cookies We Use',
      content: `**Essential Cookies**
Required for the Platform to function. These enable:
• User authentication
• Security features
• Load balancing

**Functional Cookies**
Enhance your experience by remembering:
• Language preferences
• Display settings
• Recent activity

**Analytics Cookies**
Help us understand Platform usage through:
• Page views and navigation paths
• Feature usage statistics
• Performance monitoring

**Marketing Cookies** (if enabled)
Used for:
• Measuring advertising effectiveness
• Remarketing purposes
• Conversion tracking`
    },
    {
      id: 'managing',
      title: '3. Managing Cookies',
      content: `You can control cookies through:

**Browser Settings**
Most browsers allow you to:
• View cookies stored on your device
• Block all or specific cookies
• Delete existing cookies

**Our Cookie Settings**
You may manage your preferences through our cookie consent tool (if available).

**Note**: Disabling essential cookies may prevent you from using certain Platform features.`
    },
    {
      id: 'third-party',
      title: '4. Third-Party Cookies',
      content: `Some cookies are placed by third-party services we use:

• Analytics providers
• Security services
• Payment processors

These third parties have their own privacy policies governing how they use the data collected.`
    },
    {
      id: 'updates',
      title: '5. Updates to This Policy',
      content: `We may update this Cookie Policy periodically. Changes will be posted on this page with an updated "Last Updated" date.

For questions about cookies, contact us at privacy@flippex.com.`
    }
  ]
};

export const riskDisclosure = {
  lastUpdated: 'January 2025',
  sections: [
    {
      id: 'important-notice',
      title: 'Important Notice',
      content: `**PLEASE READ THIS DOCUMENT CAREFULLY BEFORE INVESTING**

This Risk Disclosure provides important information about the risks associated with investments made through the Flippex platform. This is NOT investment advice. You should consult qualified financial, tax, and legal advisors before making any investment decisions.`
    },
    {
      id: 'no-advice',
      title: '1. Not Investment Advice',
      content: `Flippex does NOT provide:
• Personalized investment advice
• Recommendations on whether to invest
• Guarantees of investment performance

Information on the Platform is for informational purposes only. You are solely responsible for your investment decisions and should conduct your own due diligence.`
    },
    {
      id: 'no-guarantees',
      title: '2. No Guaranteed Returns',
      content: `**Projected returns are estimates, not promises.**

Actual returns may be:
• Higher than projected
• Lower than projected
• Zero or negative (total loss of capital)

Past performance of similar investments is NOT indicative of future results. Market conditions, economic factors, and project-specific circumstances can significantly impact outcomes.`
    },
    {
      id: 'real-estate-risks',
      title: '3. Real Estate Investment Risks',
      content: `Real estate investments carry specific risks including:

**Market Risk**
• Property values may decline
• Rental demand may decrease
• Local market conditions may deteriorate

**Liquidity Risk**
• Real estate is inherently illiquid
• You may not be able to sell your position before project exit
• Secondary market availability is not guaranteed

**Tenant/Occupancy Risk**
• Properties may experience vacancies
• Tenants may default on rent
• Tenant turnover affects cashflow

**Construction/Development Risk**
• Projects may face delays
• Costs may exceed budgets
• Construction quality issues may arise
• Permits or approvals may be delayed or denied

**Interest Rate Risk**
• Rising rates may affect property values
• Refinancing costs may increase
• Cap rates may expand

**Regulatory/Tax Risk**
• Laws and regulations may change
• Tax treatment may be modified
• Zoning or planning rules may affect projects`
    },
    {
      id: 'tokenization-risks',
      title: '4. Tokenization-Specific Risks',
      content: `Digital representation of investments carries additional risks:

**Technology Risk**
• Platform outages or technical failures
• Security vulnerabilities
• Software bugs or errors

**Transfer Restrictions**
• Tokens may have limited transferability
• Regulatory restrictions may apply
• No guaranteed secondary market

**Custody Risk**
• Digital assets require secure custody
• Loss of access credentials may affect holdings

**Regulatory Uncertainty**
• Tokenization regulations are evolving
• Future regulatory changes may impact operations`
    },
    {
      id: 'project-risks',
      title: '5. Project-Specific Risks',
      content: `Each project has unique risks including:

• Sponsor/operator experience and track record
• Project location and local market dynamics
• Business plan assumptions and execution
• Third-party dependencies (contractors, tenants, lenders)
• Force majeure events (natural disasters, pandemics)

**Always read the complete project documentation** including investment memoranda, risk factors, and disclosure documents before investing.`
    },
    {
      id: 'capital-at-risk',
      title: '6. Capital at Risk',
      content: `**YOU MAY LOSE SOME OR ALL OF YOUR INVESTED CAPITAL**

Investments through Flippex:
• Are NOT bank deposits
• Are NOT insured by any government agency
• Are NOT guaranteed by Flippex

Only invest amounts you can afford to lose entirely. Consider your overall financial situation and risk tolerance before investing.`
    },
    {
      id: 'jurisdiction',
      title: '7. Jurisdiction and Eligibility',
      content: `Investment availability varies by:
• Your country of residence
• Your investor status (retail, professional, accredited)
• Specific offering requirements

It is your responsibility to ensure you are eligible to invest and that doing so complies with laws in your jurisdiction.`
    },
    {
      id: 'due-diligence',
      title: '8. Conduct Your Own Due Diligence',
      content: `Before investing, you should:

• Read ALL project documentation thoroughly
• Understand the investment structure and your rights
• Review all disclosed risks and assumptions
• Assess your own financial situation and risk tolerance
• Consult qualified financial, tax, and legal advisors
• Ask questions if anything is unclear

Flippex's due diligence does NOT replace your own independent assessment.`
    }
  ]
};
